import { notification } from 'antd5';
import { ModalActionButtons, Button } from 'components/ui';

import { IMAGE_AND_METADATA_UPLOAD_OPTIONS } from 'utils/constants';
import { FileUploader } from 'components/FileUploader';
import { useAccountContext } from 'utils/account/AccountContext';
import { useGetIngestUploadUrls } from 'utils/ingest';
import {
  useNotifyInspectionImageUploadSuccessMutation,
  useNotifyInspectionImageUploadErrorMutation,
} from 'types/atlas-graphql';
import { OnCompleteStatus } from 'components/FileUploader/Modal/FileUploadModal';

const getDecriptionForDataType = (dataType: string) => {
  return dataType === IMAGE_AND_METADATA_UPLOAD_OPTIONS.SKYCRAWLER_LOG
    ? `
        A SkyCrawler inspection must have a log with the .mcap file extension. Once the log file is
        uploaded, it will be processed and released automatically. Inspection images and data will
        not be manually editable after upload.`
    : 'Upload a zipped (.zip) payload to be handled and processed by a robotics job.';
};

const getNotificationTextForDataType = (dataType: string) => {
  return dataType === IMAGE_AND_METADATA_UPLOAD_OPTIONS.SKYCRAWLER_LOG
    ? 'Skycrawler log'
    : 'Robotics package';
};

interface RoboticsUploadProps {
  inspectionId: string;
  acceptedFileType: string;
  outputDataType: string;
  onComplete: () => void;
  onBack: () => void;
}

export const RoboticsUpload: React.FunctionComponent<RoboticsUploadProps> = ({
  inspectionId,
  onComplete: onSuccessCallback,
  acceptedFileType,
  outputDataType,
  onBack,
}) => {
  const { customer } = useAccountContext();
  const organizationId = customer?.id;

  // So long as the file ends in the expected file type we should be good
  const isValidFilePath = (filename: string) => {
    return filename.toLowerCase().includes(acceptedFileType);
  };

  const [notifySuccess] = useNotifyInspectionImageUploadSuccessMutation();
  const [notifyError] = useNotifyInspectionImageUploadErrorMutation();
  const { cancelTokenSource, getIngestUploadUrls } = useGetIngestUploadUrls();

  const getUploadUrls = async (filePaths: any) => {
    if (!organizationId) {
      return undefined;
    }

    try {
      const { items } = await getIngestUploadUrls({
        recordId: inspectionId,
        dataType: outputDataType,
        files: filePaths || [],
      });

      return items;
    } catch (e) {
      return undefined;
    }
  };

  const onUploadComplete = () => {
    onSuccessCallback();
    notification.open({
      message: 'Inspection data currently processing.',
      description: 'You will receive a notification when your inspection has been processed.',
    });
  };

  const onComplete = ({ status }: { status: OnCompleteStatus }) => {
    if (status === 'CANCEL') {
      cancelTokenSource.cancel('Operation canceled by the user.');
      return;
    }
    if (status === 'SUCCESS') {
      onUploadComplete();
      notifySuccess({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: `${getNotificationTextForDataType(outputDataType)} has been successfully uploaded to your inspection with id "${inspectionId}".`,
          },
        },
      });
    }
    if (status === 'ERROR') {
      notifyError({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: `An error occurred while uploading ${getNotificationTextForDataType(outputDataType).toLowerCase()} to your inspection with id "${inspectionId}".`,
          },
        },
      });
    }
  };

  return (
    <div>
      <p>{getDecriptionForDataType(outputDataType)}</p>
      <ModalActionButtons>
        <Button data-testid="inspection-upload-cancel-btn" onClick={onBack}>
          Back
        </Button>
        <FileUploader
          id="robotics-data-upload"
          label={'Upload File'}
          acceptedFileTypes={acceptedFileType}
          multiple={false}
          directory={false}
          validation={isValidFilePath}
          onFileComplete={() => {}}
          onComplete={onComplete}
          action={getUploadUrls}
          multipartProps={{
            recordId: inspectionId,
            outputDataType,
          }}
        />
      </ModalActionButtons>
    </div>
  );
};
